// PERFORMANCE OPTIMIZATION: Heavy Agora SDK components are lazy-loaded
import React, { useEffect, useState, useRef, useMemo, Suspense, lazy } from 'react'
import { useParams } from 'react-router'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import JoinSessionRelatedButtons from 'src/consumer/components/PublicLiveSessionPage/components/JoinSessionRelatedButtons'
import AskCameraMicPermissionsDialog from 'src/consumer/components/PublicLiveSessionPage/components/AskCameraMicPermissionsDialog'
import { PUBLIC_LIVE_SESSION_PAGE_TYPES } from 'src/consumer/components/PublicLiveSessionPage/publicLiveSessionPageConstants'
import {
  AGORA_RTC_LAYOUTS,
  createPublicLocalParticipantIdentity,
  FLOW_TYPES,
  getPublicLiveSessionRtcMigrationDebugInfo,
  JoinPageHeader,
  preloadRtcProvidersByRoutingDecision,
  PUBLIC_LIVE_SESSION_APP_OWNED_AGORA_FLAG,
  logSessionRoutingDiagnostics,
  readLocalParticipantIdentity,
  readSessionInterfaceId,
  resolvePublicLiveSessionRtcMigrationEnabled,
  resolveSessionRoutingContext,
  useSessionSupportShell,
} from 'src/live'
import {
  getPublicAgoraToken,
  markBookedEventLogForPublicEvent,
} from 'src/consumer/services/featureServices'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
// AUDIO MANAGER: Singleton for meeting sounds - preloads and caches audio files
import { audioManager } from 'src/consumer/utils/audioManager'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'

// LAZY LOADED: Heavy Agora video components - only loaded when user joins call
// This saves ~800KB+ from initial page load
const LegacyAgoraVideoComponent = lazy(() =>
  import('src/consumer/components/PublicLiveSessionPage/components/AgoraVideoComponent')
)
const PublicLiveRtcVideoSurface = lazy(() =>
  import('src/live/shell/components/PublicLiveRtcVideoSurface')
)
const SessionSupportDock = lazy(() =>
  import('src/live/shell/components/SessionSupportDock')
)

const SESSION_ACCESS_CODE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/

function normalizeSessionAccessCode(value) {
  return String(value || '').trim().replace(/\s+/g, '')
}

function readInitialSessionAccessCode() {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search || '')
  return normalizeSessionAccessCode(
    params.get('code') || params.get('otp') || params.get('key') || params.get('shortKey') || '',
  )
}

function writeSessionAccessCodeToUrl(accessCode) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const accessCodeParamNames = ['code', 'otp', 'key', 'shortKey']
  accessCodeParamNames.forEach((param) => url.searchParams.delete(param))
  if (accessCode) {
    url.searchParams.set('code', accessCode)
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
}

// Video call loading fallback - shown while Agora components load
const VideoCallLoadingFallback = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      gap: 3,
    }}
  >
    <CircularProgress 
      size={48} 
      sx={{ color: '#8b5cf6' }} 
    />
    <Box
      sx={{
        color: '#ffffff',
        fontSize: '16px',
        fontWeight: 500,
        textAlign: 'center',
        opacity: 0.9,
      }}
    >
      Connecting to video call...
    </Box>
    <Box
      sx={{
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px',
        textAlign: 'center',
      }}
    >
      Please allow camera and microphone access when prompted
    </Box>
  </Box>
)

export default function PublicLiveSessionPage() {
  const { sessionSlug } = useParams()
  const initialSessionAccessCode = useMemo(() => readInitialSessionAccessCode(), [])
  const [currentScreen, setCurrentScreen] = useState(PUBLIC_LIVE_SESSION_PAGE_TYPES.joinNowScreen)

  const [agoraRTCProps, setAgoraRTCProps] = useState({})
  const [agoraRTMProps, setAgoraRTMProps] = useState({})
  const [settingUpAgora, setSettingUpAgora] = useState(false)

  const [microphoneError, setMicrophoneError] = useState(null)
  const [cameraError, setCameraError] = useState(null)
  const [availableCameras, setAvailableCameras] = useState([])
  const [availableMicrophones, setAvailableMicrophones] = useState([])
  const [availableSpeakers, setAvailableSpeakers] = useState([])
  const [selectedCamera, setSelectedCamera] = useState(null)
  const [selectedMicrophone, setSelectedMicrophone] = useState(null)
  const [selectedSpeaker, setSelectedSpeaker] = useState(null)
  const [enableAudio, setEnableAudio] = useState(true)
  const [enableVideo, setEnableVideo] = useState(true)
  const [giveBothPermissionsDialogOpen, setGiveBothPermissionsDialogOpen] = useState(false)
  const [giveMicroPhonePermissionDialogOpen, setGiveMicrophonePermissionDialogOpen] =
    useState(false)
  const [giveCamperPermissionDialogOpen, setGiveCameraPermissionDialogOpen] = useState(false)
  const [isLinkExpired, setIsLinkExpired] = useState(false)
  const [linkExpiryText, setLinkExpiryText] = useState()
  const [waitUntilInMs, setWaitUntilInMs] = useState(null)
  const [bookedEventInfo, setBookedEventInfo] = useState({})
  const [initialEngagementConfig, setInitialEngagementConfig] = useState(null) // Waiting screen engagement config (music tracks, games)
  const [clientUserLoginHint, setClientUserLoginHint] = useState(null) // Masked login hint for music access (e.g., "j***@example.com")
  const [clientUserLoginMethod, setClientUserLoginMethod] = useState(null) // Login method: 'email', 'mobile', 'google', 'facebook', 'apple'
  const [sessionAccessCode, setSessionAccessCode] = useState(initialSessionAccessCode)
  const [sessionAccessCodeDraft, setSessionAccessCodeDraft] = useState(initialSessionAccessCode)
  const [sessionAccessCodeRequired, setSessionAccessCodeRequired] = useState(false)
  const [sessionAccessCodeError, setSessionAccessCodeError] = useState('')
  
  // Guard refs to prevent repeated device checks and setup calls
  const hasCheckedDevicesRef = useRef(false)
  const setupAgoraCalledRef = useRef(false)
  
  const [supportQRCodeMediaId, setSupportQRCodeMediaId] = useState(null)

  // Dynamic share meta from session (fallback to static when no session data yet)
  const { sessionMetaTitle, sessionMetaDescription } = useMemo(() => {
    const fallbackTitle = 'Join your session | AUMHUM'
    const fallbackDesc = 'Tap to join your video session with your care provider on AUMHUM.'
    const eventName = (bookedEventInfo?.eventName || '').trim()
    const mentorName = (bookedEventInfo?.mentorName || '').trim()
    const startTime = bookedEventInfo?.startTime
    if (!eventName && !mentorName && !startTime) return { sessionMetaTitle: fallbackTitle, sessionMetaDescription: fallbackDesc }

    // Compute an absolute IST date+time prefix when the session is not today.
    // For same-day sessions the title stays succinct ("Therapy | AUMHUM");
    // for tomorrow or later we surface "16 May, 4:00 PM IST" up front so the
    // WhatsApp / iMessage preview answers "when?" without the recipient having
    // to open the link or read the message body.
    let istDateTimePrefix = ''
    let istFormattedFull = ''
    if (startTime) {
      const d = new Date(startTime)
      if (!Number.isNaN(d.getTime())) {
        const istDay = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
        const todayIst = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
        const datePart = d.toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          day: 'numeric',
          month: 'short',
        })
        const timePart = d.toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        istFormattedFull = `${datePart}, ${timePart} IST`
        if (istDay !== todayIst) {
          istDateTimePrefix = istFormattedFull
        }
      }
    }

    const maxTitleLen = 55
    let baseTitle = fallbackTitle
    if (eventName && eventName.length <= maxTitleLen) {
      baseTitle = eventName.includes('|') ? eventName : `${eventName} | AUMHUM`
    } else if (eventName && eventName.length > maxTitleLen) {
      baseTitle = `${eventName.slice(0, maxTitleLen - 10).trim()}… | AUMHUM`
    } else if (mentorName) {
      baseTitle = `Session with ${mentorName} | AUMHUM`
    }
    const title = istDateTimePrefix ? `${istDateTimePrefix} · ${baseTitle}` : baseTitle

    let description = mentorName
      ? `Join your video session with ${mentorName} on AUMHUM.`
      : fallbackDesc
    if (istFormattedFull) {
      description = `${description} Scheduled: ${istFormattedFull}.`
    }
    return { sessionMetaTitle: title, sessionMetaDescription: description }
  }, [bookedEventInfo?.eventName, bookedEventInfo?.mentorName, bookedEventInfo?.startTime])

  // AUDIO: Preload audio files on mount using shared audioManager
  // This ensures audio is cached and ready when user joins
  useEffect(() => {
    audioManager.preload()
  }, [])

  const publicRoutingContext = useMemo(() => {
    if (!bookedEventInfo?.id && !bookedEventInfo?.bookedEventId) {
      return null
    }

    return resolveSessionRoutingContext({
      flowType: FLOW_TYPES.publicLiveSession,
      sessionData: bookedEventInfo,
    })
  }, [bookedEventInfo])
  const explicitPublicSessionRoutingDecision = bookedEventInfo?.sessionRoutingDecision || null
  const sessionRoutingDecisionForPublicRtcMigration =
    explicitPublicSessionRoutingDecision || publicRoutingContext?.sessionRoutingDecision
  const isVideoCallSession = publicRoutingContext?.isVideoSession ?? true
  const publicRtcMigrationDebugInfo = useMemo(
    () =>
      getPublicLiveSessionRtcMigrationDebugInfo({
        sessionRoutingDecision: sessionRoutingDecisionForPublicRtcMigration,
      }),
    [sessionRoutingDecisionForPublicRtcMigration],
  )
  const useAppOwnedPublicRtcSurface = useMemo(
    () =>
      resolvePublicLiveSessionRtcMigrationEnabled({
        sessionRoutingDecision: sessionRoutingDecisionForPublicRtcMigration,
      }),
    [sessionRoutingDecisionForPublicRtcMigration],
  )

  useEffect(() => {
    if (!bookedEventInfo?.id && !bookedEventInfo?.bookedEventId) {
      return
    }

    const debugPayload = {
      sessionSlug,
      bookedEventId: bookedEventInfo?.bookedEventId || bookedEventInfo?.id,
      sessionMode: publicRoutingContext?.sessionMode,
      legacyPlatformId: publicRoutingContext?.legacyPlatformId,
      isVideoSession: publicRoutingContext?.isVideoSession,
      debugVersion: 'public-rtc-rollout-debug-v2',
      rawBookedEventFeatureFlags: explicitPublicSessionRoutingDecision?.featureFlags || null,
      rawBookedEventRoutingFlag:
        explicitPublicSessionRoutingDecision?.featureFlags?.[
          PUBLIC_LIVE_SESSION_APP_OWNED_AGORA_FLAG
        ] ?? null,
      ...publicRtcMigrationDebugInfo,
    }

    if (typeof window !== 'undefined') {
      window.__AUMHUM_PUBLIC_LIVE_RTC_DEBUG__ = debugPayload
    }

    console.info('[PublicLiveSessionPage] RTC surface selection', debugPayload)
  }, [
    bookedEventInfo,
    publicRoutingContext?.isVideoSession,
    publicRoutingContext?.legacyPlatformId,
    publicRoutingContext?.sessionMode,
    explicitPublicSessionRoutingDecision,
    publicRtcMigrationDebugInfo,
    sessionSlug,
  ])

  // PRELOAD HEAVY COMPONENTS: When permission dialog opens, start loading Agora SDK in background
  // This way, while user is granting camera/mic permissions, the heavy video components load
  // Result: When user clicks "Join", video call loads much faster
  useEffect(() => {
    const anyPermissionDialogOpen =
      giveBothPermissionsDialogOpen ||
      giveMicroPhonePermissionDialogOpen ||
      giveCamperPermissionDialogOpen

    if (anyPermissionDialogOpen) {
      // Start preloading heavy components while user is dealing with permissions
      // These dynamic imports start downloading the chunks but don't block UI
      if (useAppOwnedPublicRtcSurface) {
        import('src/live/shell/components/PublicLiveRtcVideoSurface').catch(() => {})
        preloadRtcProvidersByRoutingDecision(sessionRoutingDecisionForPublicRtcMigration).catch(
          () => {},
        )
      } else {
        import('src/consumer/components/PublicLiveSessionPage/components/AgoraVideoComponent')
          .catch(() => {})
      }
      import('src/live/shell/components/SessionSupportDock').catch(() => {})

      // Debug logging removed to prevent continuous logging loops
    }
  }, [
    giveBothPermissionsDialogOpen,
    giveCamperPermissionDialogOpen,
    giveMicroPhonePermissionDialogOpen,
    sessionRoutingDecisionForPublicRtcMigration,
    useAppOwnedPublicRtcSurface,
  ])
  const sessionSupportShell = useSessionSupportShell({
    enabled: Boolean(bookedEventInfo?.clinicBrandId || bookedEventInfo?.id),
    inCall: currentScreen === PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen,
    collapseDrawerForActiveCall: true,
    localUid: agoraRTCProps?.uid,
    clinicBrandId: bookedEventInfo?.clinicBrandId,
    supportQrCodeMediaId: supportQRCodeMediaId,
    initialEngagementConfig,
    sessionStartTime: bookedEventInfo?.startTime,
  })

  async function setupAgora(accessCode = sessionAccessCode) {
    // Prevent multiple calls
    if (setupAgoraCalledRef.current) {
      return
    }
    setupAgoraCalledRef.current = true
    
    const recaptchaToken = await executeRecaptchaV3('public_session_join')
    const response = await getPublicAgoraToken(sessionSlug, {
      shortKey: normalizeSessionAccessCode(accessCode),
      recaptchaToken,
    })

    const role = 'host'
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      setSessionAccessCodeRequired(false)
      setSessionAccessCodeError('')
      const agoraData = getDataMapFromCommonResponse(response)
      const routingContext = resolveSessionRoutingContext({
        flowType: FLOW_TYPES.publicLiveSession,
        sessionData: agoraData,
      })
      const resolvedSessionRouting = routingContext.sessionRoutingDecision
      const legacyPlatformId = routingContext.legacyPlatformId
      const canonicalMeetingSrc = agoraData?.meetingSrc || legacyPlatformId
      const isVideoSession = routingContext.isVideoSession

      logSessionRoutingDiagnostics({
        label: 'PublicLiveSessionPage',
        flowType: FLOW_TYPES.publicLiveSession,
        sessionData: agoraData,
        sessionRoutingDecision: resolvedSessionRouting,
        context: {
          bookedEventId: agoraData?.bookedEventId,
          sessionSlug,
        },
      })
      
      // For offline meetings, skip ALL device permission checks to avoid browser prompts
      if (!isVideoSession) {
        // Explicitly set empty device lists and no errors for offline meetings
        setAvailableCameras([])
        setAvailableMicrophones([])
        setAvailableSpeakers([])
        setMicrophoneError(false)
        setCameraError(false)
        hasCheckedDevicesRef.current = true // Mark as checked to prevent any further attempts
      } else if (!hasCheckedDevicesRef.current) {
        // Only check for camera/mic devices if meeting is not offline AND we haven't checked yet
        hasCheckedDevicesRef.current = true // Set flag immediately to prevent repeated calls
        try {
          // DYNAMIC IMPORT: checkAvailableDevices is loaded on-demand to avoid blocking initial render
          const { checkAvailableDevices } = await import(
            'src/live/shell/devicePermissions/checkAvailableDevices'
          )
          await checkAvailableDevices(
            setMicrophoneError,
            setCameraError,
            setAvailableCameras,
            setAvailableMicrophones,
            setAvailableSpeakers,
            setSelectedCamera,
            setSelectedMicrophone,
            setSelectedSpeaker,
            setGiveBothPermissionsDialogOpen,
          )
        } catch (error) {
          // Silently fail if permission check fails
          console.log('Skipping device permission check:', error)
        }
      }
      
      // Only enable audio/video if permissions are granted (no errors)
      const shouldEnableAudio = enableAudio && !microphoneError
      const shouldEnableVideo = enableVideo && !cameraError
      const resolvedVideoQuality =
        agoraData?.sessionRoutingDecision?.videoQuality || agoraData?.videoQuality || null
      
      setAgoraRTCProps({
        appId: import.meta.env.VITE_AGORA_APP_ID,
        channel: agoraData?.bookedEventId,
        uid: parseInt(agoraData?.userName),
        token: agoraData?.rtcToken,
        layout: AGORA_RTC_LAYOUTS.pin,
        role,
        enableDualStream: resolvedVideoQuality?.enableDualStream === true,
        enableAudio: shouldEnableAudio,
        enableVideo: shouldEnableVideo,
        cameraDeviceId: shouldEnableVideo ? selectedCamera : undefined,
        microphoneDeviceId: shouldEnableAudio ? selectedMicrophone : undefined,
        videoQuality: resolvedVideoQuality,
      })
      setAgoraRTMProps({
        username: agoraData?.eventUserName ? agoraData?.eventUserName : 'Patient',
        displayUsername: true,
        token: response?.data?.data?.rtmToken,
        showPopUpBeforeRemoteMute: false,
        isThisUserAllowedToMuteOthers: false,
      })
      setBookedEventInfo({
        id: agoraData?.bookedEventId,
        endTime: agoraData?.endTime,
        startTime: agoraData?.startTime,
        eventName: agoraData?.eventName,
        eventType: agoraData?.eventType,
        mentorName: agoraData?.mentorName,
        // Support both mentorProfilePicId and mentorProfilePic from API response
        mentorProfilePicId: agoraData?.mentorProfilePicId || agoraData?.mentorProfilePic,
        clinicBrandId: agoraData?.clinicBrandId,
        meetingSrc: canonicalMeetingSrc,
        legacyPlatformId,
        sessionRoutingDecision:
          agoraData?.sessionRoutingDecision || routingContext.sessionRoutingDecision,
        sessionInterfaceId: readSessionInterfaceId(
          agoraData,
          FLOW_TYPES.publicLiveSession,
        ),
        localParticipantIdentity: readLocalParticipantIdentity(
          agoraData,
          createPublicLocalParticipantIdentity(agoraData),
        ),
        sessionMode: routingContext.sessionMode,
        rtcProviderResolved: routingContext.rtcProviderResolved,
        signalingProviderResolved: routingContext.signalingProviderResolved,
      })
      
      // Extract login hint and method if available (for music access)
      if (agoraData?.clientUserLoginHint) {
        setClientUserLoginHint(agoraData.clientUserLoginHint)
      }
      if (agoraData?.clientUserLoginMethod) {
        setClientUserLoginMethod(agoraData.clientUserLoginMethod)
      }
      
      // Extract support QR code media ID from API response (preferred)
      if (agoraData?.supportQrCodeMediaId) {
        setSupportQRCodeMediaId(agoraData.supportQrCodeMediaId)
      }
      
      // For offline meetings, ensure we don't show video call controls
      if (!isVideoSession) {
        setEnableAudio(false)
        setEnableVideo(false)
      }
    } else {
      if (response?.data?.code === 4041 || response?.data?.data?.requiresShortKey) {
        setupAgoraCalledRef.current = false
        setSessionAccessCodeRequired(true)
        setIsLinkExpired(false)
        setWaitUntilInMs(null)
        setSessionAccessCodeError(
          normalizeSessionAccessCode(accessCode)
            ? 'That session code did not work.'
            : 'Enter the session code to continue.',
        )
        return
      }
      if (response?.data?.code === 8000 || response?.data?.code === 42942) {
        setupAgoraCalledRef.current = false
        setIsLinkExpired(true)
        setWaitUntilInMs(null)
        setLinkExpiryText(
          response?.data?.message || 'We could not verify this request. Please refresh and try again.',
        )
        return
      }
      if (response?.data?.code === 5002) {
        setIsLinkExpired(true)
      }
      if (response?.data?.code === 48764) {
        const data = getDataMapFromCommonResponse(response)
        if (!data?.waitUntilInMs) {
          setIsLinkExpired(true)
          setLinkExpiryText(
            'This link is not valid yet. Please wait for a few minutes before joining.',
          )
          setWaitUntilInMs(null)
          return
        }

        setIsLinkExpired(true)
        // Convert relative duration (ms to wait) to absolute timestamp (when it expires)
        // The API returns waitUntilInMs as a relative duration, but the countdown expects an absolute timestamp
        setWaitUntilInMs(Date.now() + data.waitUntilInMs)
        setLinkExpiryText('This link will be available in:')
        
        // Extract mentor info from error response if available
        if (data?.mentorName || data?.mentorProfilePicId || data?.mentorProfilePic) {
          setBookedEventInfo(prev => ({
            ...prev,
            mentorName: data.mentorName || prev.mentorName,
            mentorProfilePicId: data.mentorProfilePicId || data.mentorProfilePic || prev.mentorProfilePicId,
          }))
        }
        
        // Extract engagement config for waiting screen (music tracks, games)
        // API returns this when waitUntilInMs > 2 minutes
        // Structure: data.engagement = { music: {...}, games: {...} }
        if (data?.engagement) {
          setInitialEngagementConfig(data.engagement)
        }
        
        // Extract login hint and method if available (for music access)
        if (data?.clientUserLoginHint) {
          setClientUserLoginHint(data.clientUserLoginHint)
        }
        if (data?.clientUserLoginMethod) {
          setClientUserLoginMethod(data.clientUserLoginMethod)
        }
        
        // Extract support QR code media ID from API response (for early-join error responses)
        if (data?.supportQrCodeMediaId) {
          setSupportQRCodeMediaId(data.supportQrCodeMediaId)
        }
      }
    }
  }

  async function handleSessionAccessCodeSubmit(event) {
    event.preventDefault()
    const normalizedCode = normalizeSessionAccessCode(sessionAccessCodeDraft)
    if (!normalizedCode || !SESSION_ACCESS_CODE_PATTERN.test(normalizedCode)) {
      setSessionAccessCodeError('Enter a valid session code.')
      return
    }

    setSessionAccessCode(normalizedCode)
    writeSessionAccessCodeToUrl(normalizedCode)
    setSessionAccessCodeError('')
    setSettingUpAgora(true)
    setupAgoraCalledRef.current = false
    await setupAgora(normalizedCode)
    setSettingUpAgora(false)
  }

  function handleLeaveRoom() {
    markBookedEventLogForPublicEvent(sessionSlug, 'LEAVE', {
      camera: enableVideo,
      mic: enableAudio,
    })
    setCurrentScreen(PUBLIC_LIVE_SESSION_PAGE_TYPES.joinNowScreen)
  }

  // Track if we're waiting for permissions before joining
  const [pendingJoin, setPendingJoin] = useState(false)
  // Ref to prevent double JOIN log calls (React state updates are async/batched)
  const joinLogCalledRef = useRef(false)
  
  async function joinCall() {
    // Reset the ref when starting a new join attempt
    joinLogCalledRef.current = false

    if (!isVideoCallSession) {
      if (joinLogCalledRef.current) return
      joinLogCalledRef.current = true

      markBookedEventLogForPublicEvent(sessionSlug, 'JOIN', {
        camera: enableVideo,
        mic: enableAudio,
      })
      setCurrentScreen(PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen)
      return
    }
    
    // Check if we need to ask for permissions first
    try {
      const [micPermission, camPermission] = await Promise.all([
        navigator.permissions.query({ name: 'microphone' }).catch(() => ({ state: 'prompt' })),
        navigator.permissions.query({ name: 'camera' }).catch(() => ({ state: 'prompt' })),
      ])
      
      const needsPermissions = 
        micPermission.state === 'prompt' || 
        micPermission.state === 'denied' ||
        camPermission.state === 'prompt' ||
        camPermission.state === 'denied'
      
      if (needsPermissions) {
        // Show permission dialog first
        setPendingJoin(true)
        setGiveBothPermissionsDialogOpen(true)
        return
      }
    } catch (error) {
      // If permission query fails, show dialog to be safe
      setPendingJoin(true)
      setGiveBothPermissionsDialogOpen(true)
      return
    }
    
    // Permissions already granted, proceed with join
    // Guard against double calls using ref (synchronous check)
    if (joinLogCalledRef.current) return
    joinLogCalledRef.current = true
    
    markBookedEventLogForPublicEvent(sessionSlug, 'JOIN', {
      camera: enableVideo,
      mic: enableAudio,
    })
    setCurrentScreen(PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen)
  }

  async function handleRefreshStatusForUser() {
    await markBookedEventLogForPublicEvent(sessionSlug, 'REFRESH', {
      camera: enableVideo,
      mic: enableAudio,
    })
  }

  async function setup() {
    setSettingUpAgora(true)
    await Promise.all([setupAgora()])
    setSettingUpAgora(false)
  }

  useEffect(() => {
    setup()
  }, [])

  useEffect(() => {
    let interval = null
    if (currentScreen === PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen) {
      handleRefreshStatusForUser()
      interval = setInterval(() => {
        handleRefreshStatusForUser()
      }, 180000)
    }
    return () => {
      clearInterval(interval)
    }
  }, [enableVideo, enableAudio, currentScreen])

  const sessionAccessCodeCard = (
    <Box
      component="form"
      onSubmit={handleSessionAccessCodeSubmit}
      sx={{
        width: '100%',
        maxWidth: '430px',
        background: '#ffffff',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        borderRadius: '8px',
        boxShadow: '0 18px 50px rgba(15, 23, 42, 0.12)',
        padding: { xs: '22px', sm: '28px' },
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
          Session code required
        </Typography>
        <Typography sx={{ marginTop: '6px', fontSize: '14px', lineHeight: 1.6, color: '#64748b' }}>
          Enter the code shared with this meeting invite.
        </Typography>
      </Box>
      <TextField
        label="Session code"
        value={sessionAccessCodeDraft}
        onChange={(event) => {
          setSessionAccessCodeDraft(event.target.value)
          setSessionAccessCodeError('')
        }}
        autoFocus
        fullWidth
        inputProps={{
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: 'false',
          maxLength: 32,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyOutlinedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {sessionAccessCodeError && (
        <Alert severity="error" sx={{ borderRadius: '8px' }}>
          {sessionAccessCodeError}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        disabled={settingUpAgora}
        sx={{
          minHeight: '48px',
          borderRadius: '8px',
          background: '#0f766e',
          fontWeight: 800,
          textTransform: 'none',
          '&:hover': { background: '#115e59' },
        }}
      >
        {settingUpAgora ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : 'Continue'}
      </Button>
    </Box>
  )

  return (
    <Box
      className="d-flex flex-column"
      sx={{
        height: '100vh',
        maxHeight: '100vh',
        overflowX: 'hidden',
        // Allow scrolling when engagement is enabled (content can be taller than viewport)
        // or always allow on mobile, but allow on desktop when engagement is present
        overflowY: sessionSupportShell.engagementConfig ? 'auto' : { xs: 'auto', lg: 'hidden' },
        WebkitOverflowScrolling: 'touch',
        // Subtle AUMHUM-brand wash — a violet/indigo radial tint
        // layered over the neutral grey gradient so the /v/ session
        // page reads as part of the marketing site family without
        // disturbing the high-contrast needs of the in-call UI.
        background:
          'radial-gradient(ellipse 40rem 24rem at 85% -10%, rgba(124, 58, 237, 0.08), transparent 60%), ' +
          'radial-gradient(ellipse 40rem 24rem at -10% 10%, rgba(99, 102, 241, 0.06), transparent 60%), ' +
          'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 50%, #f0f2f5 100%)',
        position: 'relative',
      }}
    >
      {/*
        Session join pages are short-lived and personal — they're
        not SEO targets and shouldn't appear in search results.
        We still want proper OG tags so the shared link previews
        cleanly in WhatsApp / iMessage / Slack etc.
      */}
      <SEOComponent
        title={sessionMetaTitle}
        description={sessionMetaDescription}
        image={APP_CONSTANTS.APP_LOGO_LINK}
        type="website"
        noIndex
      />
      {currentScreen === PUBLIC_LIVE_SESSION_PAGE_TYPES.joinNowScreen && (
        <>
          <Box
            className="d-flex flex-column"
            sx={{
              padding: { xs: '12px', sm: '16px', md: '24px' },
              maxWidth: '1200px',
              margin: '0 auto',
              width: '100%',
              gap: { xs: '12px', sm: '16px', md: '24px' },
              // When engagement is enabled, allow content to expand naturally (no fixed height)
              // Otherwise, maintain original behavior (100% height on desktop)
              minHeight: sessionSupportShell.engagementConfig ? { xs: 'auto', lg: 'auto' } : { xs: 'auto', lg: '100%' },
              height: sessionSupportShell.engagementConfig ? { xs: 'auto', lg: 'auto' } : { xs: 'auto', lg: '100%' },
            }}
          >
            <JoinPageHeader
              userRole="user"
              bookedEventFinalData={
                isLinkExpired
                  ? null
                  : {
                      ...bookedEventInfo,
                      meetingSrc: bookedEventInfo?.legacyPlatformId || 'aumhum',
                    }
              }
            />
            <Box
              sx={{
                flexDirection: 'column',
                gap: 0,
                flexGrow: { xs: 0, lg: 1 },
                minHeight: { xs: 'auto', lg: 0 },
                background: 'transparent',
                display: 'flex',
                justifyContent: { xs: 'flex-start', lg: 'center' },
                alignItems: 'center',
                width: '100%',
                padding: { xs: '8px', sm: '16px', md: '24px' },
              }}
            >
              {sessionAccessCodeRequired ? (
                sessionAccessCodeCard
              ) : (
                <JoinSessionRelatedButtons
                  setCurrentScreen={setCurrentScreen}
                  cameraError={cameraError}
                  microphoneError={microphoneError}
                  userHasAllowedAudio={enableAudio}
                  userHasAllowedVideo={enableVideo}
                  handleJoinRoom={joinCall}
                  setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                  setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
                  setUserHasAllowedAudio={setEnableAudio}
                  setUserHasAllowedVideo={setEnableVideo}
                  tokenLoading={settingUpAgora}
                  availableCameras={availableCameras}
                  availableMicrophones={availableMicrophones}
                  selectedCamera={selectedCamera}
                  selectedMicrophone={selectedMicrophone}
                  setSelectedCamera={setSelectedCamera}
                  setSelectedMicrophone={setSelectedMicrophone}
                  isVideoCallScreen={isVideoCallSession}
                  showTermsText
                  isLinkExpired={isLinkExpired}
                  linkExpiryText={linkExpiryText}
                  waitUntilInMs={waitUntilInMs}
                  engagementConfig={sessionSupportShell.engagementConfig}
                  sessionSlug={sessionSlug}
                  shortKey={sessionAccessCode}
                  clientUserLoginHint={clientUserLoginHint}
                  clientUserLoginMethod={clientUserLoginMethod}
                />
              )}
            </Box>
          </Box>
        </>
      )}
      {/* VIDEO CALL SCREEN - Lazy loaded components wrapped in Suspense */}
      {/* This entire section only loads when user clicks "Join" button */}
      {currentScreen === PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen && (
        <>
          {/* Agora Video Component - Heavy, lazy loaded with loading fallback */}
          {/* Only render when Agora props are fully initialized to prevent SDK errors */}
          <Suspense fallback={<VideoCallLoadingFallback />}>
            {useAppOwnedPublicRtcSurface ? (
              <PublicLiveRtcVideoSurface
                bookedEventInfo={bookedEventInfo}
                sessionRoutingDecision={
                  bookedEventInfo?.sessionRoutingDecision ||
                  sessionRoutingDecisionForPublicRtcMigration
                }
                agoraRtcProps={agoraRTCProps}
                agoraRtmProps={agoraRTMProps}
                handleLeaveRoom={handleLeaveRoom}
                isFullHeight
                providerFallback={<VideoCallLoadingFallback />}
                onCameraClose={() => {
                  setEnableVideo(false)
                  markBookedEventLogForPublicEvent(sessionSlug, 'CAMERA_OFF', {
                    camera: false,
                    mic: enableAudio,
                  })
                }}
                onCameraOpen={() => {
                  setEnableVideo(true)
                  markBookedEventLogForPublicEvent(sessionSlug, 'CAMERA_ON', {
                    camera: true,
                    mic: enableAudio,
                  })
                }}
                onAudioClose={() => {
                  setEnableAudio(false)
                  markBookedEventLogForPublicEvent(sessionSlug, 'MIC_OFF', {
                    camera: enableVideo,
                    mic: false,
                  })
                }}
                onAudioOpen={() => {
                  setEnableAudio(true)
                  markBookedEventLogForPublicEvent(sessionSlug, 'MIC_ON', {
                    camera: enableVideo,
                    mic: true,
                  })
                }}
                onUserJoin={sessionSupportShell.handleRemoteUserJoin}
                onUserLeave={sessionSupportShell.handleRemoteUserLeave}
              />
            ) : agoraRTCProps?.appId ? (
              <LegacyAgoraVideoComponent
                agoraRtcProps={agoraRTCProps}
                agoraRtmProps={agoraRTMProps}
                handleLeaveRoom={handleLeaveRoom}
                isFullHeight
                onCameraClose={() => {
                  setEnableVideo(false)
                  markBookedEventLogForPublicEvent(sessionSlug, 'CAMERA_OFF', {
                    camera: false,
                    mic: enableAudio,
                  })
                }}
                onCameraOpen={() => {
                  setEnableVideo(true)
                  markBookedEventLogForPublicEvent(sessionSlug, 'CAMERA_ON', {
                    camera: true,
                    mic: enableAudio,
                  })
                }}
                onAudioClose={() => {
                  setEnableAudio(false)
                  markBookedEventLogForPublicEvent(sessionSlug, 'MIC_OFF', {
                    camera: enableVideo,
                    mic: false,
                  })
                }}
                onAudioOpen={() => {
                  setEnableAudio(true)
                  markBookedEventLogForPublicEvent(sessionSlug, 'MIC_ON', {
                    camera: enableVideo,
                    mic: true,
                  })
                }}
                onUserJoin={sessionSupportShell.handleRemoteUserJoin}
                onUserLeave={sessionSupportShell.handleRemoteUserLeave}
              />
            ) : (
              <VideoCallLoadingFallback />
            )}
          </Suspense>
          
          {/* Waiting/Support Drawer - Separate Suspense so it loads independently */}
          {/* This shows immediately while video is still loading */}
          <Suspense fallback={null}>
            <SessionSupportDock
              renderDrawer={sessionSupportShell.showDrawer}
              drawerOpen={sessionSupportShell.showDrawer}
              showCollapsedStrip={sessionSupportShell.showCollapsedStrip}
              onCollapse={() => {
                sessionSupportShell.setIsDrawerCollapsed(true)
              }}
              onExpand={() => {
                sessionSupportShell.setIsDrawerCollapsed(false)
              }}
              sessionStartTime={bookedEventInfo?.startTime}
              mentorName={bookedEventInfo?.mentorName}
              mentorProfilePicId={bookedEventInfo?.mentorProfilePicId}
              hasMentorJoined={sessionSupportShell.hasMentorJoined}
              showOnlySupport={sessionSupportShell.showOnlySupport}
              isCollapsed={sessionSupportShell.isDrawerCollapsed}
              supportQRCodeMediaId={sessionSupportShell.supportQRCodeMediaId}
              engagementConfig={sessionSupportShell.engagementConfig}
              sessionSlug={sessionSlug}
              shortKey={sessionAccessCode}
            />
          </Suspense>
        </>
      )}
      <AskCameraMicPermissionsDialog
        giveBothPermissionsDialogOpen={giveBothPermissionsDialogOpen}
        setGiveBothPermissionsDialogOpen={setGiveBothPermissionsDialogOpen}
        giveMicrophonePermissionDialogOpen={giveMicroPhonePermissionDialogOpen}
        setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
        giveCameraPermissionDialogOpen={giveCamperPermissionDialogOpen}
        setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
        setMicrophoneError={setMicrophoneError}
        setCameraError={setCameraError}
        setAvailableMicrophones={setAvailableMicrophones}
        setSelectedMicrophone={setSelectedMicrophone}
        setAvailableSpeakers={setAvailableSpeakers}
        setSelectedSpeaker={setSelectedSpeaker}
        setAvailableCameras={setAvailableCameras}
        setSelectedCamera={setSelectedCamera}
        sessionType={bookedEventInfo?.eventType === 'therapy' ? 'therapy' : 'consultation'}
        onPermissionsGranted={() => {
          // If we were waiting to join, proceed now that permissions are granted
          // Use ref guard to prevent double calls (React state updates are async)
          if (pendingJoin && !joinLogCalledRef.current) {
            joinLogCalledRef.current = true
            setPendingJoin(false)
            markBookedEventLogForPublicEvent(sessionSlug, 'JOIN', {
              camera: enableVideo,
              mic: enableAudio,
            })
            setCurrentScreen(PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen)
          }
        }}
        onSkipPermissions={() => {
          // User chose to continue without camera/mic - still proceed with the call
          // Use ref guard to prevent double calls (React state updates are async)
          if (pendingJoin && !joinLogCalledRef.current) {
            joinLogCalledRef.current = true
            setPendingJoin(false)
            
            // IMPORTANT: Disable audio/video to prevent Agora SDK from trying to create tracks
            setEnableAudio(false)
            setEnableVideo(false)
            
            // Update Agora RTC props to disable audio/video SYNCHRONOUSLY before screen change
            // Using functional update and deferring screen change to ensure props are applied first
            setAgoraRTCProps(prev => ({
              ...prev,
              enableAudio: false,
              enableVideo: false,
              // Force no device IDs when skipping permissions
              cameraDeviceId: undefined,
              microphoneDeviceId: undefined,
            }))
            
            markBookedEventLogForPublicEvent(sessionSlug, 'JOIN', {
              camera: false,
              mic: false,
            })
            
            // Defer screen change to ensure state updates are applied first
            // This prevents Agora SDK from trying to create tracks with old props
            setTimeout(() => {
              setCurrentScreen(PUBLIC_LIVE_SESSION_PAGE_TYPES.videoCallScreen)
            }, 0)
          }
        }}
      />
    </Box>
  )
}
